import type { BuildContext, Fragment } from "../types.js";
import { scaleFor } from "../format.js";
import { backgroundGradient } from "../primitives/backgroundGradient.js";
import { brandLogo } from "../primitives/brandLogo.js";
import { hookText } from "../primitives/hookText.js";
import { cta } from "../primitives/cta.js";
import { card } from "../primitives/card.js";
import { safeArea } from "../primitives/safeArea.js";
import { getLocale, type LocaleCode } from "../../locales/index.js";
import { listBangerSchema, type ListBangerProps } from "../../templates/index.js";
import { colors } from "../../brand/theme.js";
import { escapeHtml } from "../composition.js";
import { assemble } from "./shared.js";

export function listBangerDurationSec(props: ListBangerProps): number {
  return 1.5 + props.items.length * 2 + 2.5;
}

export function buildListBangerHtml(
  rawProps: unknown,
  ctx: Omit<BuildContext, "durationSec">,
): string {
  const props = listBangerSchema.parse(rawProps) as ListBangerProps;
  const loc = getLocale(ctx.locale as LocaleCode);
  const ctaText = props.cta || loc.ctas[props.ctaKey as keyof typeof loc.ctas];

  const totalSec = listBangerDurationSec(props);
  const fullCtx: BuildContext = { ...ctx, durationSec: totalSec };

  const hookSize = scaleFor(ctx.format, { base: 84, short: 96, square: 76, wide: 90 });
  const itemSize = scaleFor(ctx.format, { base: 52, short: 60, square: 48, wide: 56 });
  const ctaSize = scaleFor(ctx.format, { base: 50, short: 60, square: 46, wide: 54 });
  const logoSize = scaleFor(ctx.format, { base: 80, short: 100, square: 80, wide: 90 });

  const bg = backgroundGradient(fullCtx, "bg", { variant: "primary", animated: true });

  const itemStartFrames = 30;
  const itemDurationFrames = 60;
  const hookStart = 0;
  const hookDur = totalSec;

  const logoFrag = brandLogo({ id: "tpl-logo", size: logoSize, color: "white" });
  const hookFrag = hookText({ id: "tpl-hook", text: props.hook, startSec: hookStart, size: hookSize, color: "#fff" });

  const ctaStartFrames = itemStartFrames + props.items.length * itemDurationFrames + 20;
  const ctaStart = ctaStartFrames / 30;
  const ctaDur = Math.max(2, totalSec - ctaStart);
  const ctaFrag = cta({ id: "tpl-cta", text: ctaText, startSec: ctaStart, durationSec: ctaDur, size: ctaSize });

  const itemFragments: Fragment[] = [];
  const itemHtmls: string[] = [];
  props.items.forEach((item, i) => {
    const startSec = (itemStartFrames + i * itemDurationFrames) / 30;
    const durSec = Math.max(0.5, totalSec - startSec);
    const id = `tpl-item-${i}`;
    const cardInner = `<div style="display:flex;align-items:center;gap:28px;">
  <div style="flex:0 0 auto;width:${itemSize * 1.6}px;height:${itemSize * 1.6}px;border-radius:9999px;background:linear-gradient(135deg, ${colors.primary}, ${colors.accent});display:flex;align-items:center;justify-content:center;font-weight:900;font-size:${itemSize}px;color:white;">${i + 1}</div>
  <div style="font-weight:700;font-size:${itemSize}px;color:white;line-height:1.2;">${escapeHtml(item)}</div>
</div>`;
    const itemCard = card({ id, childrenHtml: cardInner, startSec, padding: 36, variant: "filled" });
    itemFragments.push(itemCard);
    itemHtmls.push(
      `<div id="${id}-clip" class="clip" data-start="${startSec}" data-duration="${durSec}" data-track-index="${i + 1}">${itemCard.html}</div>`,
    );
  });

  const innerHtml = `
<div style="display:flex;justify-content:flex-start;">${logoFrag.html}</div>
<div style="margin-top:36px;">${hookFrag.html}</div>
<div id="tpl-items" style="display:flex;flex-direction:column;gap:22px;margin-top:36px;flex:1;">
${itemHtmls.join("\n")}
</div>
<div id="tpl-cta-clip" class="clip" data-start="${ctaStart}" data-duration="${ctaDur}" data-track-index="${props.items.length + 2}" style="display:flex;justify-content:center;margin-top:24px;">${ctaFrag.html}</div>
`;

  const safe = safeArea({ id: "tpl-safe", format: ctx.format, childrenHtml: innerHtml });

  return assemble({
    templateId: "ListBanger",
    ctx: fullCtx,
    background: bg,
    contentFragments: [logoFrag, hookFrag, ...itemFragments, ctaFrag],
    safeAreaInnerHtml: innerHtml,
    safeArea: safe,
  });
}
